<template>
  <div>
    <!-- Desktop view: table -->
    <table class="table is-fullwidth is-striped is-narrow is-hidden-touch bottom-border">
      <thead>
        <tr>
          <th></th>
          <th class="right-border"></th>
          <th
            class="has-text-centered is-vcentered is-size-7 has-background-info-light top-border right-border"
          >
            All
          </th>
          <template v-for="(group, groupIndex) in permissionGroups" :key="groupIndex">
            <th
              :colspan="group.permissions.length"
              class="has-text-centered is-vcentered is-size-7 has-background-info-light top-border right-border"
              style="padding: 0 10px"
            >
              {{ group.name }}
            </th>
          </template>
        </tr>
        <tr>
          <th></th>
          <th class="has-text-centered is-vcentered is-size-7 right-border">Email Address</th>
          <th class="right-border"></th>
          <template v-for="group in permissionGroups" :key="group.name">
            <template v-for="permission in group.permissions" :key="permission.key">
              <th
                class="has-text-centered is-vcentered is-size-7"
                style="white-space: pre"
                :class="getBorderClass(permission.key, group.permissions)"
              >
                {{ permission.label }}
              </th>
            </template>
          </template>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(member, index) in allMembers" :key="member.id || `new[${index}]`">
          <td class="is-pulled-right">
            <!-- Delete button for existing members; add button for new members -->
            <button
              v-if="member.id"
              class="button is-small is-danger"
              title="Remove Member"
              @click="deleteMember(member)"
            >
              <span class="icon is-small">
                <i class="fas fa-times"></i>
              </span>
            </button>
            <button
              v-if="!member.id"
              class="button is-primary is-small"
              title="Add Member"
              @click="addMember"
            >
              <span class="icon is-small">
                <i class="fas fa-plus"></i>
              </span>
            </button>
          </td>
          <td class="is-vcentered right-border">
            <!-- Display email or input for new member -->
            <span v-if="member.id">{{ member.email }}</span>
            <span v-else>
              <input
                v-model="member.email"
                class="input is-small"
                type="email"
                placeholder="New member email"
                style="min-width: 17ch"
                @keyup.enter="addMember"
              />
            </span>
          </td>
          <!-- Permissions checkboxes -->
          <td class="is-vcentered right-border">
            <input
              :checked="isAllPermissionsChecked(member)"
              :indeterminate="isSomePermissionsChecked(member) && !isAllPermissionsChecked(member)"
              title="Toggle all permissions"
              type="checkbox"
              @change="toggleAllPermissions(member, $event.target.checked)"
            />
          </td>
          <template v-for="group in permissionGroups" :key="group.name">
            <template v-for="permission in group.permissions" :key="permission.key">
              <td
                class="has-text-centered is-vcentered"
                :class="getBorderClass(permission.key, group.permissions)"
              >
                <input
                  v-model="member.permissions[permission.key]"
                  :title="member.id ? 'Update permissions' : 'Configure new member permissions'"
                  type="checkbox"
                  @change="member.id && updateMember(member)"
                />
              </td>
            </template>
          </template>
        </tr>
      </tbody>
    </table>

    <!-- Mobile view: cards -->
    <div class="is-hidden-desktop">
      <div v-for="(member, index) in allMembers" :key="member.id || `new[${index}]`" class="box">
        <!-- Member info -->
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <strong>Email:</strong>
              <div v-if="member.id" style="margin-left: 0.5rem">
                {{ member.email }}
              </div>
              <div v-else style="margin-left: 0.5rem">
                <input
                  v-model="member.email"
                  class="input"
                  type="email"
                  placeholder="Email Address"
                  @keyup.enter="addMember"
                />
              </div>
            </div>
          </div>
          <div v-if="member.id" class="level-right">
            <div class="level-item">
              <button class="button is-small is-danger" @click="deleteMember(member)">
                <span class="icon is-small">
                  <i class="fas fa-times"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
        <!-- Permissions checkboxes -->
        <div class="columns is-multiline">
          <div class="column is-full">
            <label class="checkbox">
              <input
                :checked="isAllPermissionsChecked(member)"
                :indeterminate="
                  isSomePermissionsChecked(member) && !isAllPermissionsChecked(member)
                "
                title="Toggle all permissions"
                type="checkbox"
                @change="toggleAllPermissions(member, $event.target.checked)"
              />
              All permissions
            </label>
          </div>
          <div
            v-for="(group, groupIndex) in permissionGroups"
            :key="groupIndex"
            class="column is-half"
          >
            <strong>{{ group.name }}</strong>
            <div v-for="permission in group.permissions" :key="permission.key">
              <label class="checkbox">
                <input
                  v-model="member.permissions[permission.key]"
                  type="checkbox"
                  @change="member.id && updateMember(member)"
                />
                {{ permission.label }}
              </label>
            </div>
          </div>
        </div>
        <div v-if="!member.id">
          <button class="button is-primary is-small" @click="addMember">Add Member</button>
        </div>
      </div>
    </div>
  </div>
  <br v-if="!canAddMember" class="is-hidden-desktop" />
  <p v-if="!canAddMember" class="well">
    You've reached the maximum number of members for your plan.
    <a :href="contactPath">Contact us</a> to add more members.
  </p>
</template>

<script setup>import { computed as _computed, ref as _ref } from 'vue';

  import { reactive } from "vue";
  import { Flash } from "~/jose_modern/flash";
  import { subscriptionMemberPath, subscriptionMembersPath, contactPath } from "~/routes";
  import { errorHelpTip } from "../lib/constants";

  // For inventory team members, these two permission fields are always hardcoded as such.
  // These correspond to `COARSE_PERMISSIONS` in the backend.
  const HARDCODED_PERMISSIONS = {
    inventory: true,
    designer: false,
  };

  const props = defineProps({
    members: { type: Array, required: true },
    permissionGroups: { type: Array, required: true },
    subscriptionId: { type: Number, required: true },
    initialCanAddMember: { type: Boolean, required: true },
  });

  const permissionsList = _computed(() => {
    return props.permissionGroups.flatMap((group) => group.permissions.map((perm) => perm.key));
  });

  const members = _ref([...props.members]);
  const newMember = reactive({
    id: null,
    email: "",
    permissions: Object.fromEntries(permissionsList.value.map((perm) => [perm, false])),
  });

  let canAddMember = _ref(props.initialCanAddMember);
  const allMembers = _computed(() => {
    return canAddMember.value ? [...members.value, newMember] : members.value;
  });

  const isAllPermissionsChecked = (member) => {
    return permissionsList.value.every((key) => member.permissions[key] === true);
  };

  const isSomePermissionsChecked = (member) => {
    return permissionsList.value.some((key) => member.permissions[key] === true);
  };

  const toggleAllPermissions = async (member, checked) => {
    permissionsList.value.forEach((key) => {
      member.permissions[key] = checked;
    });

    // If this is an existing member, update on the server
    if (member.id) {
      await updateMember(member);
    }
  };

  const addMember = async () => {
    const email = newMember.email.trim();
    if (!email) {
      console.debug("Email is blank, not adding new member");
      return;
    }
    const permissions = { ...newMember.permissions };

    const url = subscriptionMembersPath();
    const data = {
      subscription_member: {
        email,
        subscription_id: props.subscriptionId,
        ...permissions,
        ...HARDCODED_PERMISSIONS,
      },
    };

    const json = await sendRequest(url, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (json) {
      if (json.member) {
        if (typeof json.can_add_member !== "undefined") {
          canAddMember.value = json.can_add_member;
        } else {
          console.error(`Added member, but missing can_add_member: ${JSON.stringify(json)}`);
        }
        members.value.push({
          id: json.member.id,
          email: json.member.email,
          permissions,
        });
        resetNewMember();
      } else {
        console.error(`Added member, but response was missing member: ${JSON.stringify(json)}`);
      }
    }
  };

  const updateMember = async (member) => {
    const url = subscriptionMemberPath(member.id);
    const data = {
      subscription_member: {
        ...member.permissions,
        ...HARDCODED_PERMISSIONS,
      },
    };
    await sendRequest(url, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  };

  const deleteMember = async (member) => {
    if (!confirm(`Are you sure you want to remove ${member.email}?`)) return;

    const url = subscriptionMemberPath(member.id);
    const json = await sendRequest(url, {
      method: "DELETE",
    });

    if (json) {
      if (typeof json.can_add_member !== "undefined") {
        canAddMember.value = json.can_add_member;
      } else {
        console.error("Deleted member, but response does not define can_add_member");
      }

      const index = members.value.findIndex((m) => m.id === member.id);
      if (index !== -1) {
        members.value.splice(index, 1);
      }
    }
  };

  const resetNewMember = () => {
    newMember.email = "";
    Object.keys(newMember.permissions).forEach((key) => {
      newMember.permissions[key] = false;
    });
  };

  const flashSuccess = (msg) => {
    Flash.hideExisting();
    Flash.success(msg, { ignoreThrottle: true });
  };
  const flashError = (msg) => Flash.error(msg);

  async function sendRequest(url, options = {}) {
    const defaultHeaders = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };
    const response = await fetch(url, {
      headers: defaultHeaders,
      ...options,
    });

    let json = {};
    try {
      json = await response.json();
    } catch (e) {
      flashError(`An unexpected error occurred. ${errorHelpTip}`);
      console.error("Unexpected error response:", response);
      return null;
    }

    if (response.ok) {
      flashSuccess(json.notice || "Updated successfully.");
    } else {
      const errorMessage = json.error;
      if (errorMessage) {
        flashError(`${errorMessage}.`);
      } else {
        flashError(`An unexpected error occurred. ${errorHelpTip}`);
        console.error("Unexpected error response:", json);
      }
    }

    return json;
  }

  function getBorderClass(permissionKey, groupPermissions) {
    return permissionKey === groupPermissions[groupPermissions.length - 1].key
      ? "right-border"
      : "";
  }
</script>

<style scoped>
  .top-border {
    border-top: 2px solid #e7ecef;
  }
  .right-border {
    border-right: 2px solid #e7ecef;
  }
  .bottom-border {
    border-bottom: 2px solid #e7ecef;
  }
</style>
